import React, { useEffect, useState } from "react";
import { Content } from "../../components/Layout";
import { ActionText, AttacmentBox, BorderedField, Box, Flex } from "../../components/Flex";
import { Label, LoadingCircle, Paragraph, Title } from "../../components/Text";
import { Button } from "../../components/Button";
import { CloseIcon } from "../../icons/CloseIcon";
import { JIRA_URL } from "../../settings";
import { Scrollbar } from "../../components/Scrollbar";
import { IssueUserIcon } from "../../icons/IssueUserIcon";
import { formatDate } from "../../utils";
import { SortBlockArrowIcon } from "../../icons/SortBlockArrowIcon";
import { PadlockIcon } from "../../icons/PadlockIcon";
import { FormTooltip } from "../../components/Tooltip";
import { IssueAttachment, IssueComment } from "../../components/Input";
import { Link } from "react-router-dom";
import { ClipIcon } from "../../icons/ClipIcon";


const IssueDetails = ({
    data={},
    loading,
    addComment,
    onClose,
    pingServer,
    addAttachment,
    downloadAttachment,
    deleteAttachment,
    updateAttachment,
    isAdmin,
    renderFieldContent
}) => {
    const initialState = {
        sorted: true,
        focus: false,
        comment: null,
        visible: true,
        selection: [0, 0]
    };
    const [ state, setState ] = useState(initialState);
    const NOFORMAT = "{noformat}";

    useEffect(() => {
        let interval;
        if (state.focus) {
            pingServer();
            interval = setInterval(() => {
                // if a comment is being written ping server every 10 minutes to refresh the token
                pingServer();
            }, 600000)
        }

        return () => clearInterval(interval);
    }, [ state.focus ]);

    const fields = {
        details: [
            { label: "Reporter", attr: "submittedBy", render: (text, record) => <Link to={(isAdmin ? "" : `/organisations/${record.orgAlias}`) + `/users/${record.email}`}>{text}</Link> },
            { label: "Email", attr: "email", render: (text) => <a href={"mailto: "+text}>{text}</a> },
            { label: "Organization", attr: "orgName", nowrap: true, render: (text, record) => <Link to={"/organisations/"+record.orgAlias}>{text}</Link> },
            { label: "Created", attr: "submitDatetime" },
            { label: "Status", attr: "status"},
            { label: "OS version", attr: "osversion" },
            { label: "System Architecture", attr: "sysarch" },
            { label: "Product", attr: "product" },
            { label: "Product Version", attr: "version" }
        ],
        link: [
            { label: "Jira Link", attr: "ticketRef", render: d => <a target="_blank" rel="noreferrer" href={`${JIRA_URL}/browse/${d}`} >{`${JIRA_URL}/browse/${d}`}</a> }
        ]
    };

    const sortComments = (comments, order) => {
        let sorted = comments.slice(0).sort((a, b) => a.created.localeCompare(b.created));
        return order
            ? sorted.reverse()
            : sorted
    };

    const renderComments = (comments) => {
        return Array.isArray(comments)
            ? sortComments(comments, state.sorted).map((comment) =>(
                <Flex key={comment.key} gap={{ right: 15 }}>
                    <IssueUserIcon style={{ alignItems: "flex-start", paddingTop: "7px"}} />
                    <Flex direction="column" padding={{ top: "7px" }}>
                        <Flex align="center" margin={{ bottom: "7px" }}>
                            <Title style={{ marginRight: "8px" }}>{comment.author}</Title>
                            <Paragraph lineHeight="22px" style={{ marginRight: "20px" }}>{formatDate(comment.created, "MMMM D, YYYY, h:mm A")}</Paragraph>
                            {isAdmin &&
                            <FormTooltip text={`Visible to ${comment.visibility ? "all" : "VSI"} users`}>
                                <PadlockIcon size={16} locked={!comment.visibility} style={{ color: "var(--sp-grey-blue-color)" }} />
                            </FormTooltip>}
                        </Flex>
                        <Paragraph breakWord whiteSpace="pre-line">
                            {comment.body.split(NOFORMAT).map((text, idx) => (
                                idx % 2 !== 0
                                    ? <code key={`text-block-${idx}`}>{text}</code>
                                    : <span key={`text-block-${idx}`}>{text}</span>
                            ))}
                        </Paragraph>
                    </Flex>
                </Flex>
            ))
            : <Flex gap={{ right: 15 }}>
                <LoadingCircle />
                <Paragraph isLoading width="100%" />
            </Flex>
    };

    const cropFilename = (str) => {
        return str.length > 15
          ? str.substr(0, 7) + '...' + str.substr(str.length-6, str.length)
          : str
    };

    const renderAttachments = (attachments) => {
        let isLoading = !Array.isArray(attachments);
        return (
            <Flex direction="column">
                <Flex justify="space-between" align="center">
                    <Paragraph color="var(--sp-label-color)" lineHeight="22px">Attachments{Array.isArray(attachments) ? " (" + attachments.length + ")" : ""}:</Paragraph>
                    <span>
                        <IssueAttachment disabled={isLoading} onFileChange={(file) => addAttachment([file])}>
                            <ActionText disabled={isLoading}>
                                <ClipIcon />Add attachment
                            </ActionText>
                        </IssueAttachment>
                    </span>
                </Flex>
                <Flex wrap="wrap" margin={{ left: "-8px", top: 0, right: "-8px", bottom: 0 }} style={{ textAlign: "center" }}>
                    {isLoading
                        ? Array(3).fill(null).map((row, idx) => <AttacmentBox key={`loading-box-${idx}`} loading />)
                        : attachments.map((attachment) => (
                            <AttacmentBox
                                key={attachment.id}
                                removeAttachment={attachment.available
                                    ? () => deleteAttachment({
                                        content: <Paragraph>Are you sure you want to delete <b>{attachment.filename}</b>?</Paragraph>,
                                        id: attachment.id})
                                    : null}
                                downloadAttachment={() => downloadAttachment(attachment.id, attachment.filename)}
                            >
                                <Label>{formatDate(attachment.created, "MMM D, YYYY, h:mm A")}</Label>
                                <Flex justify="center" gap={{ right: 4 }}>
                                    <Paragraph title={attachment.filename}>{cropFilename(attachment.filename)}</Paragraph>
                                    {isAdmin && <PadlockIcon size={16} locked={attachment.visible != 1} onClick={() => isAdmin && updateAttachment(attachment.id, attachment.visible == 0)} style={{ color: "var(--sp-grey-blue-color)" }} />}
                                </Flex>
                            </AttacmentBox>
                        ))}
                </Flex>
            </Flex>
        );
    };

    const onTextSelect = (e) => {
        e.persist();
        if (e.target.selectionStart !== state.selection[0] || e.target.selectionEnd !== state.selection[1]) {
            setState(state => ({ ...state, selection: [e.target.selectionStart, e.target.selectionEnd]}));
        };
    };

    const formatCode = () => {
        const { comment, selection } = state;
        let start = selection[0], end = selection[1],
        newComment = comment || "";
        newComment = newComment.slice(0, start) + NOFORMAT + newComment.slice(start, end) + NOFORMAT + newComment.slice(end, newComment.length);
        setState(state => ({ ...state, comment: newComment }))
    };


    return(
        <Content background="#FFFFFF">
            <Flex width="100%" justify="space-between">
                <Title h4 isLoading={loading} color="var(--sp-label-color)">
                    [{data.ticketRef}]
                </Title>
                <CloseIcon onClick={onClose} />
            </Flex>
            <Flex height="100%" gap={{ right: 17 }}>
                <Flex direction="column" flex={1}>
                    <Scrollbar>
                        <Flex direction="column" padding={{ right: "16px" }} gap={{ bottom: 16 }}>
                            <Title h4 isLoading={loading}>{data.summary}</Title>
                            <Flex direction="column">
                                <Title h6 capitalize margin={{ bottom: "4px" }}>description</Title>
                                {isAdmin && renderFieldContent(fields.link, data)}
                                {data && data.description && data.description.split("\n").map((para, idx) => (
                                    <Paragraph key={`desc-block-${idx}`} margin={{ bottom: "4px" }}>
                                        {para}
                                    </Paragraph>
                                ))}
                            </Flex>
                            {renderAttachments(data.attachments)}
                            <Flex flex={1} justify="space-between" padding={{ bottom: "7px" }} style={{ marginBottom: 0 }}>
                                <Label bold>comments</Label>
                                <Flex align="center" gap={{ right: 5 }} onClick={() => setState(state => ({ ...state, sorted: !state.sorted }))} cursor="pointer">
                                    <Label>
                                        <SortBlockArrowIcon size={20} descend={state.sorted} />
                                    </Label>
                                    <Label bold>
                                        {state.sorted ? "new" : "old"}est first
                                    </Label>
                                </Flex>
                            </Flex>
                            {state.focus
                                ? <Flex margin={{ bottom: "16px" }} gap={{ right: 15 }} padding={{ right: "12px", top: "16px" }} style={{ borderTop: "2px solid #D7E3F7" }}>
                                    <IssueUserIcon commenting />
                                    <Flex direction="column" flex={1} gap={{ bottom: 12 }} width="calc(100% - 47px)">
                                        <IssueComment
                                            text={state.comment}
                                            pingServer={pingServer}
                                            onTextChange={value => setState(state => ({ ...state, comment: value }))}
                                            onTextSelect={onTextSelect}
                                            formatCode={formatCode}
                                        />
                                        <Flex align="center" justify="space-between">
                                            <Flex gap={{ right: 16 }}>
                                                <Button disabled={state.comment === null} onClick={() => { addComment({ comment: state.comment, visible: state.visible }); setState(initialState)}}>Save</Button>
                                                <Button link onClick={() => setState(state => ({ ...state, comment: null, focus: false }))}>Cancel</Button>
                                            </Flex>
                                            {isAdmin &&
                                            <Flex align="center" gap={{ right: 8 }}>
                                                <Label bold lineHeight="20px">visible to {state.visible ? "all" : "VSI"} users</Label>
                                                <Label>
                                                    <PadlockIcon
                                                        size={16}
                                                        locked={!state.visible}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setState(state => ({ ...state, visible: !state.visible }))}
                                                    />
                                                </Label>
                                            </Flex>}
                                        </Flex>
                                    </Flex>
                                    </Flex>
                                : <Flex margin={{ bottom: "16px" }} gap={{ right: 15 }} padding={{ top: "16px" }} background="#FFFFFF" position="sticky" style={{ zIndex: 1, bottom: "0px", borderTop: "2px solid #D7E3F7" }}>
                                    <IssueUserIcon commenting />
                                    <BorderedField
                                        flex={1}
                                        padding={{ top: "10px", left: "12px", right: "12px", bottom: "10px" }}
                                        onClick={() => setState(state => ({ ...state, focus: true }))}
                                    >
                                        <Paragraph>Add a comment...</Paragraph>
                                    </BorderedField>
                                </Flex>}
                            <Flex direction="column" gap={{ bottom: 16 }}>
                                {renderComments(data.comments)}
                            </Flex>
                        </Flex>
                    </Scrollbar>
                </Flex>
                <Box flatten background="#F0F4FB" gap={{ bottom: 8 }} width="390px">
                    <Title h6>Details</Title>
                    {renderFieldContent(fields.details, data)}
                </Box>
            </Flex>
        </Content>
    );
};

export { IssueDetails }